import { Dialog } from '@strata/react-dialog';
import { TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { getIn, setIn, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { DynamicFields } from 'components/valueCase/DynamicFields';
import {
    compareProp,
    ConditionTypesForDisplay,
    getConditionFilter,
    getParamsInitialValues,
    markdownLinksHelperText,
    StopPropagation,
} from 'components/valueCase/helpers';
import { conditionTypeValidationMessage } from 'pages/valueCase/helpers/valueCaseValidationSchema';

export const EditConditionDialog = ({
    id = '',
    openConditionDialog = '',
    setOpenConditionDialog,
    conditionBlueprints = [],
}) => {
    const formik = useFormikContext();
    const [initialContent, setInitialContent] = useState();

    const uniqueId =
        getIn(formik.values, `${id}.distinctId`) ?? getIn(formik.values, `${id}.condition_id`);

    const isOpen = uniqueId === openConditionDialog;

    useEffect(() => {
        if (!initialContent && isOpen) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInitialContent(getIn(formik.values, id));
        }
    }, [formik.values, id, initialContent, isOpen]);

    const mapConditionObjectToForm = (condObj) => ({
        name: condObj.condition_name,
        secondaryData: ConditionTypesForDisplay[condObj.condition_type],
        ...condObj,
    });

    const idPathArray = id.split('.');
    const subtaskPath = idPathArray.slice(0, idPathArray.length - 2).join('.');
    const subtaskConditionType = getIn(formik.values, `${subtaskPath}.subtask_type`);

    const conditionFilter =
        subtaskConditionType ?? getConditionFilter(formik, subtaskPath, uniqueId);

    const adjustParams = async (blueprints) => {
        const componentsPath = `${id}.components`;
        const paramsPath = `${id}.params`;
        const currentConditionTypeId = getIn(formik.values, `${id}.condition_type_id`);
        const { components } = blueprints.filter(
            (bp) => bp.condition_type_id === currentConditionTypeId,
        )[0];
        const params = getIn(formik.values, paramsPath);
        await Promise.all(
            components.map((component, index) => {
                const componentPath = `${componentsPath}.${index}`;
                const paramKeys = component.configuredParams;
                return Object.keys(paramKeys)
                    .map((key) => {
                        const configKey = paramKeys[key];
                        const configValue = params[configKey];
                        return formik.setFieldValue(
                            `${componentPath}.configuredParams.${key}`,
                            configValue,
                        );
                    })
                    .flat();
            }),
        );
    };

    return (
        <div {...StopPropagation}>
            <Dialog
                dialogTitle="Add/edit Condition"
                isOpen={isOpen}
                secondaryButtonLabel="Cancel"
                onSecondaryButtonClick={async () => {
                    setInitialContent();
                    setOpenConditionDialog();
                    await formik.setFieldValue(id, initialContent);
                }}
                primaryButtonLabel="Save"
                onPrimaryButtonClick={async () => {
                    setInitialContent();
                    setOpenConditionDialog();
                    await adjustParams(conditionBlueprints);
                }}
                isPrimaryButtonDisabled={!!getIn(formik.errors, id)}
                width={1500}
                dialogContent={
                    <>
                        <div className="padding__bottom--medium">
                            {`Define the condition below. For label definitions, ${markdownLinksHelperText.toLowerCase()}`}
                        </div>
                        <TextInputFormik
                            id={`${id}.condition_title`}
                            label="Condition Name"
                            placeholderText="Enter Condition Name"
                            helperText="This name is for configuration purposes only and will not be displayed to the user."
                            required
                        />
                        <SuggestSingleSelectFormik
                            id={`${id}.condition_name`}
                            label="Requirement Component"
                            placeholderText="Select Requirement Component"
                            requiredValidationMessage={getIn(formik.errors, `${id}.condition_type`)}
                            required
                            disableAutoPopulate
                            values={conditionBlueprints
                                .filter((blueprint) => {
                                    // Allow user to select a different condition if they selected a different subtask type.
                                    if (
                                        getIn(formik.errors, `${id}.condition_type`) ===
                                        conditionTypeValidationMessage
                                    ) {
                                        return true;
                                    }
                                    if (conditionFilter) {
                                        return blueprint.condition_type === conditionFilter;
                                    }
                                    return true;
                                })
                                .sort(compareProp('condition_name'))
                                .map((condObj) => mapConditionObjectToForm(condObj))}
                            onChange={async (newValue) => {
                                await formik.setFieldValue(
                                    `${id}.params`,
                                    getParamsInitialValues(newValue.configurable_params),
                                );
                                formik.setTouched(setIn(formik.touched, `${id}.params`, {}));
                                await formik.setFieldValue(`${id}.condition_name`, newValue.name);
                                await formik.setFieldValue(
                                    `${id}.condition_type`,
                                    newValue.condition_type,
                                );
                                if (!conditionFilter) {
                                    await formik.setFieldValue(
                                        `${subtaskPath}.subtask_type`,
                                        newValue.condition_type,
                                    );
                                }
                                await formik.setFieldValue(
                                    `${id}.condition_type_id`,
                                    newValue.condition_type_id,
                                );
                                await formik.setFieldValue(
                                    `${id}.is_validated`,
                                    newValue.is_validated,
                                );
                                await formik.setFieldValue(
                                    `${id}.use_component_validation`,
                                    newValue.use_component_validation,
                                );
                                await formik.setFieldValue(
                                    `${id}.components`,
                                    newValue.components.map(
                                        ({ configuredParams: _confParams, ...rest }) => ({
                                            ...rest,
                                        }),
                                    ),
                                );
                            }}
                            selectedItem={
                                conditionBlueprints
                                    .filter(
                                        (condObj) =>
                                            condObj.condition_type_id ===
                                            getIn(formik.values, `${id}.condition_type_id`),
                                    )
                                    .map((condObj) => condObj.condition_name)[0]
                            }
                        />
                        {getIn(formik.values, `${id}.condition_type_id`) && (
                            <DynamicFields
                                configurableParams={
                                    conditionBlueprints.filter(
                                        (condObj) =>
                                            condObj.condition_type_id ===
                                            getIn(formik.values, `${id}.condition_type_id`),
                                    )[0]?.configurable_params
                                }
                                fieldPath={`${id}.params`}
                            />
                        )}
                    </>
                }
            />
        </div>
    );
};

EditConditionDialog.propTypes = {
    id: PropTypes.string,
    openConditionDialog: PropTypes.string,
    setOpenConditionDialog: PropTypes.func.isRequired,
    conditionBlueprints: PropTypes.arrayOf(PropTypes.shape({})),
};
